import { Component, Input } from "@angular/core";
import { Contact, ContactField } from "@models";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { SuccessComponent } from "../success.interface";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { CryptoTransaction } from "@models/transaction";
import { AccountService } from "@modules/account/services/account.service";
import { ContactService } from "@main-services/contact.service";
import { FiatService } from "@main-services/currency";
import { SegmentService } from "@main-services/segment.service";
import { ContactEditComponent } from "../../../contact/contact-edit-modal/contact-edit.component";
import { RudderstackService } from "@main-services/rudderstack.service";

@Component({
	selector: "send-success",
	templateUrl: "./send-success.component.html",
	styleUrls: ["./../success-screen.component.scss"],
	standalone: false
})
export class SendSuccessComponent extends SuccessComponent {
	public _transaction: CryptoTransaction = null;
	public contact: Contact = null;

	protected readonly flowName: string = "Send Assets";
	protected readonly pathPrepend: string = "/send/asset";

	public constructor(
		protected router: Router,
		private contactService: ContactService,
		protected accountService: AccountService,
		protected tradableListService: TradableListService,
		protected fiatService: FiatService,
		protected segmentService: SegmentService,
		protected rudderstackService: RudderstackService
	) {
		super(fiatService, tradableListService, accountService, router, segmentService, rudderstackService);
	}

	@Input()
	public set transaction(transaction: CryptoTransaction) {
		this._transaction = transaction;
		this.contact$.subscribe((c) => (this.contact = c));
	}

	public get transaction(): CryptoTransaction {
		return this._transaction;
	}

	public get contact$(): Observable<Contact> {
		return this.contactService.fetchContact(this.transaction.getContactId());
	}

	public getCorrectSendRecipient(): string {
		if (this.contact) {
			if (this.contact.isDeleted()) {
				return this.transaction.getRecipient();
			}
			return this.contact.getName();
		}
		return this.transaction.getRecipient();
	}

	public goToWallet(): void {
		this.close();
		setTimeout(() => {
			// The Backbutten handling in Modals screws this if you dont delay it a little
			this.router.navigateByUrl("/wallet/" + this.transaction.getWalletId());
		}, 300);
	}

	public showAddContactButton(): boolean {
		if (!this.transaction || !this.contact) {
			return false;
		}
		return !this.isContactAlreadySaved && (this.transaction.isOpenInvitation() || this.contact.isDeleted());
	}

	public addAsContact(): void {
		if (!this.contact) {
			throw new Error("Unable to add this Contact");
		}
		const newContact = new Contact();

		if (this.isContactAlreadySaved) {
			newContact.setName(this.contact.getName());
		} else {
			newContact.setName("");
		}

		// Don't show other asset addresses - show only the one to which you sent coins
		const filteredFields = this.contact.getFields().filter((cf) => {
			let isDifferentCoinAddress: boolean = false;
			if (
				(this.currentCryptoTransferredField &&
					cf.getValue() !== this.currentCryptoTransferredField.getValue() &&
					cf.getType().includes("address")) ||
				(!this.currentCryptoTransferredField && cf.getType().includes("address")) ||
				(this.currentCryptoTransferredField &&
					cf.getValue() === this.currentCryptoTransferredField.getValue() &&
					cf.getType() !== this.currentCryptoTransferredField.getType())
			) {
				isDifferentCoinAddress = true;
			}
			return !isDifferentCoinAddress;
		});
		newContact.setFields(filteredFields);

		this.close();
		setTimeout(() => {
			// The Backbutten handling in Modals screws this if you dont delay it a little
			this.modalService.create(ContactEditComponent, { data: newContact }).present();
		}, 300);
	}

	private get currentCryptoTransferredField(): ContactField {
		const coinName: string = this.tradableListService
			.getItemById(this.transaction.getCurrencyId())
			.getSymbol()
			.toLowerCase();
		const currentContactField: ContactField = this.contact
			.getFields()
			.find((cf) => cf.getType().startsWith(coinName));
		return currentContactField;
	}

	public get isContactAlreadySaved(): boolean {
		if (!this.contact) {
			return false;
		}

		const savedContact = this.contactService.contacts.find(
			(c) => c.getId() === this.contact.getId() && !this.contact.isDeleted()
		);

		return !!savedContact;
	}
}
